import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {StyledProjectWarningMissingBanner} from '@/features/projects/components/project-warning-missing-banner/style'
import {Trans, useTranslation} from '@/translations/i18n'
import {PatientExtended} from '@/features/patients/types'
import {CreateMedicalRecordButton} from '@/features/medical-records/components/create-medical-record-button/CreateMedicalRecordButton'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {generateCreateMedicalReportPath} from '@/features/medical-reports/utils'
import {useNavigate} from 'react-router-dom'
import {useGetMedicalReportTypes} from '@/features/medical-reports/queries/useGetMedicalReportTypes'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {usePermissions} from '@/features/auth/store/store'

export const ProjectWarningMissingTeamReport = ({
    projectId,
    patient
}: {
    projectId: string
    patient: PatientExtended
}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const permissions = usePermissions()
    // TODO: this should be emproved by adding a new endpoint to get the medical report type by project type or something else
    const {data: medicalReportTypes, isLoading} = useGetMedicalReportTypes({
        search: 'Relazione d’equipe'
    })

    const medicalReportType = medicalReportTypes?.[0]?.types.find(type => type.keyName == 'team_report')
    const hasMedicalRecord = !!patient.medicalRecord

    const handleNavigateToMedicalRecord = () => {
        if (!medicalReportType) return

        const path = generateCreateMedicalReportPath(patient.id, projectId, {
            id: medicalReportType.id,
            name: medicalReportType.name,
            keyName: medicalReportType.keyName,
            groupName: medicalReportType.groupName,
            groupKeyName: medicalReportType.groupKeyName
        })
        if (path) {
            navigate(path)
        }
    }

    return (
        <StyledProjectWarningMissingBanner align="center">
            <Flexbox gap={1} align="center">
                <AlertCircleIcon />
                <Text palette="neutral" shade="950" size="textSm">
                    <Trans
                        i18nKey="projects:missing_relation_content"
                        components={{b: <b />}}
                        values={{
                            relation: t('medical_records:type_team_report')
                        }}
                    />
                </Text>
                {permissions?.medicalRecords.write && (
                    <>
                        {isLoading ? (
                            <Spinner size={16} />
                        ) : hasMedicalRecord ? (
                            <Button onClick={handleNavigateToMedicalRecord} variant="tertiary" size="sm">
                                {t('commons:add')}
                            </Button>
                        ) : (
                            <CreateMedicalRecordButton
                                patient={patient}
                                navigateToCreateReport={true}
                                variant="tertiary"
                                size="sm"
                                hideIcon
                                onSuccess={() => {
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})
                                }}
                                searchParams={{
                                    projectId,
                                    medicalReportType
                                }}
                            >
                                {t('commons:add')}
                            </CreateMedicalRecordButton>
                        )}
                    </>
                )}
            </Flexbox>
        </StyledProjectWarningMissingBanner>
    )
}
